import { Component, Input, OnInit, EventEmitter } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { MultiSelectConfirmDismissComponent } from './multi-select-confirm-dismiss/multi-select-confirm-dismiss.component';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { cloneDeep } from 'lodash-es';
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(utc);
dayjs.extend(timezone);

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
})
export class MultiSelectComponent implements OnInit {

  @Input() blueshiftsInput: Blueshift[];
  @Input() modalId: string;
  @Input() positionDetail: PositionDetail;

  blueshifts: Blueshift[];
  directInviteBlueshifts: Blueshift[];
  selectedJobIdsAndShifts = new Map();
  monthsWithBlueshifts: dayjs.Dayjs[];

  constructor(private readonly modalCtrl: ModalController) { }

  ngOnInit() {
    this.blueshifts = cloneDeep(this.blueshiftsInput);
    this.directInviteBlueshifts = this.blueshifts.filter(blueshift => blueshift.isDirectInvite);
    this.monthsWithBlueshifts = this.getMonthsForBlueshifts();
  }

  async onDismiss() {
    // if one or more blueshifts selected
    if (this.selectedJobIdsAndShifts.size > 0) {
      // show multi-select-confirm-dismiss as modal
      const eventEmitter = new EventEmitter();
      const m = await this.modalCtrl.create({
        component: MultiSelectConfirmDismissComponent,
        cssClass: 'bottom-sheet-modal-presented-above-modal',
        componentProps: {
          numShiftsSelected: this.selectedJobIdsAndShifts.size,
          confirmDismissEvent: eventEmitter,
        }
      });
      eventEmitter.subscribe(() => {
        this.modalCtrl.dismiss();
      });
      await m.present();
    } else {
      // no blueshifts selected, just dismiss
      this.modalCtrl.dismiss();
    }
  }

  handleBlueshiftClickEvent(event) {
    if (event.blueshift.isSelected) {
      this.selectedJobIdsAndShifts.set(event.blueshift.externalJobId, event.blueshift);
      this.addOverlapsForSelectedBlueshift(event.blueshift);
    } else {
      this.selectedJobIdsAndShifts.delete(event.blueshift.externalJobId);
      this.removeOverlapsForDeselectedBlueshift(event.blueshift);
    }
  }

  getSelectedBlueshiftsSortedByStartTime() {
    const selectedBlueshifts = Array.from(this.selectedJobIdsAndShifts.values());
    const sortedSelectedBlueshifts = selectedBlueshifts.sort(function(b1: Blueshift, b2: Blueshift) {
      if (dayjs(b1.startDateTime).isBefore(dayjs(b2.startDateTime))) {
        return -1;
      } else if(dayjs(b2.startDateTime).isBefore(dayjs(b1.startDateTime))) {
        return 1;
      } else {
        return 0;
      }
    });
    return sortedSelectedBlueshifts;
  }

  getDirectInviteBannerMessaging() {
    const numDirectInviteShifts = this.directInviteBlueshifts.length;
    return `${this.positionDetail.companyName} has direct invited you to ${numDirectInviteShifts === 1 ? 
      '1 shift' : numDirectInviteShifts + ' shifts'}`;
  }

  private addOverlapsForSelectedBlueshift(selectedShift: Blueshift) {
    const startTimeWithBuffer = dayjs(selectedShift.startDateTime).subtract(6, 'hour');
    const endTimeWithBuffer = dayjs(selectedShift.endDateTime).add(6, 'hour');

    this.blueshifts.forEach(blueshift => {
      if (blueshift.externalJobId !== selectedShift.externalJobId) {
        if (this.doTimeRangesOverlap(
            dayjs(blueshift.startDateTime),
            dayjs(blueshift.endDateTime),
            startTimeWithBuffer,
            endTimeWithBuffer)) {
          blueshift.doesConflictWithOtherShift = true;
        }
      }
    });
  }

  private removeOverlapsForDeselectedBlueshift(deselectedShift: Blueshift) {
    const startTimeWithBuffer = dayjs(deselectedShift.startDateTime).subtract(6, 'hour');
    const endTimeWithBuffer = dayjs(deselectedShift.endDateTime).add(6, 'hour');

    this.blueshifts.forEach(blueshift => {
      if (blueshift.externalJobId !== deselectedShift.externalJobId) {
        if (this.doTimeRangesOverlap(
            dayjs(blueshift.startDateTime),
            dayjs(blueshift.endDateTime),
            startTimeWithBuffer,
            endTimeWithBuffer)) {
          blueshift.doesConflictWithOtherShift = false;
        }
      }
    });
  }

  private doTimeRangesOverlap(
    start1: dayjs.Dayjs, end1: dayjs.Dayjs, 
    start2: dayjs.Dayjs, end2: dayjs.Dayjs): boolean {
      return start1.isSameOrBefore(end2) && end1.isSameOrAfter(start2);
  }

  private getMonthsForBlueshifts(): dayjs.Dayjs[] {
    let months = [];
    this.blueshifts.forEach(blueshift => {
      const blueshiftMonth = dayjs(blueshift.startDateTime).startOf('month');
      if (!months.find(month => month.isSame(blueshiftMonth))) {
        months.push(blueshiftMonth);
      }
    });
    return months;
  }

}

export class Blueshift {
  doesConflictWithExistingJob: boolean;
  doesConflictWithOtherShift?: boolean = false;
  isSelected?: boolean = false;
  externalJobId: string;
  startDateTime: string;
  endDateTime: string;
  wage: number;
  isDirectInvite?: boolean = false;
}

export class PositionDetail {
  companyName: string;
  positionId: number;
  positionTitle: string;
}
